/**
 * Iris Notification Center — Cloud 7 Strategic substrate.
 *
 * Bell-icon button in the app bar. Polls /api/v2/iris-tasks; renders a badge
 * with the active (queued/running) task count, opens a popover with
 * in-flight progress bars + recently-completed tasks (with link-to-result on
 * click) + dismiss buttons.
 *
 * No existing Iris op is queue-backed yet — this slice ships the substrate
 * so future ops can opt in. Empty popover state explains that.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle,
  ErrorOutline,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { irisTasksAPI, type IrisTask, type IrisTaskStatus } from '../services/api';

const POLL_INTERVAL_OPEN_MS = 3000;
const POLL_INTERVAL_BACKGROUND_MS = 15000;

const STATUS_LABEL: Record<IrisTaskStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const formatTaskType = (type: string) =>
  type
    .split('_')
    .map((s) => (s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s))
    .join(' ');

const IrisNotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<IrisTask[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Prevent overlapping polls if a request runs long.
  const inFlightRef = useRef(false);

  const open = Boolean(anchorEl);

  const fetchTasks = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const data = await irisTasksAPI.list();
      setTasks(data);
      setError(null);
    } catch (err: any) {
      // 401/403 will redirect to login via the global interceptor — don't
      // surface a noisy notification-center error in that case.
      const status = err?.response?.status;
      if (status !== 401 && status !== 403) {
        setError('Failed to load Iris tasks');
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Background poll cadence — slows when popover closed, speeds up when open.
  useEffect(() => {
    fetchTasks();
    const interval = setInterval(
      fetchTasks,
      open ? POLL_INTERVAL_OPEN_MS : POLL_INTERVAL_BACKGROUND_MS
    );
    return () => clearInterval(interval);
  }, [open, fetchTasks]);

  const activeCount = useMemo(
    () => tasks.filter((t) => t.status === 'queued' || t.status === 'running').length,
    [tasks]
  );

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleDismiss = async (taskId: number) => {
    setLoading(true);
    try {
      await irisTasksAPI.dismiss(taskId);
      await fetchTasks();
    } finally {
      setLoading(false);
    }
  };

  const handleViewResult = (task: IrisTask) => {
    if (task.link_to) {
      navigate(task.link_to);
      handleClose();
    }
  };

  const renderTaskIcon = (status: IrisTaskStatus) => {
    if (status === 'completed') return <CheckCircle color="success" fontSize="small" />;
    if (status === 'failed') return <ErrorOutline color="error" fontSize="small" />;
    if (status === 'cancelled') return <CancelIcon color="disabled" fontSize="small" />;
    return <CircularProgress size={16} />;
  };

  return (
    <>
      <Tooltip title={activeCount > 0 ? `${activeCount} Iris task(s) running` : 'Iris notifications'}>
        <IconButton
          onClick={handleOpen}
          aria-label={`Iris notifications (${activeCount} active)`}
          aria-haspopup="true"
          aria-expanded={open || undefined}
          size="small"
        >
          <Badge badgeContent={activeCount} color="primary" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight="bold">Iris Tasks</Typography>
          {activeCount > 0 && (
            <Typography variant="caption" color="text.secondary">{activeCount} active</Typography>
          )}
        </Box>
        <Divider />
        {error && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}
        {!error && tasks.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No Iris tasks running right now.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Long-running Iris operations will appear here as they queue and complete.
            </Typography>
          </Box>
        )}
        {!error && tasks.length > 0 && (
          <List dense disablePadding>
            {tasks.map((task) => {
              const isActive = task.status === 'queued' || task.status === 'running';
              const isDismissable = !isActive;
              const isClickable = task.status === 'completed' && !!task.link_to;
              return (
                <ListItem
                  key={task.id}
                  divider
                  alignItems="flex-start"
                  sx={{
                    py: 1.25,
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : {},
                  }}
                  onClick={() => isClickable && handleViewResult(task)}
                  secondaryAction={
                    isDismissable ? (
                      <IconButton
                        edge="end"
                        size="small"
                        disabled={loading}
                        aria-label="Dismiss"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismiss(task.id);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <Box sx={{ mr: 1.5, mt: 0.5 }}>{renderTaskIcon(task.status)}</Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {formatTaskType(task.task_type)}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {task.progress_message || STATUS_LABEL[task.status]}
                        </Typography>
                        {isActive && (
                          <LinearProgress
                            variant="determinate"
                            value={task.progress}
                            sx={{ mt: 0.5, height: 4, borderRadius: 1 }}
                          />
                        )}
                        {task.status === 'failed' && task.error_message && (
                          <Typography variant="caption" color="error" component="span" sx={{ display: 'block', mt: 0.5 }}>
                            {task.error_message}
                          </Typography>
                        )}
                        {isClickable && (
                          <Button
                            size="small"
                            sx={{ mt: 0.5, p: 0, minWidth: 0, textTransform: 'none' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewResult(task);
                            }}
                          >
                            View result →
                          </Button>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default IrisNotificationCenter;
